import { createContext, useContext, useState, ReactNode } from "react";

interface OrgScopeContextType {
  orgId: string;
  setOrgId: (id: string) => void;
  // undefined = All (default — param omitted, today's OR-match behavior),
  // true = Initiated (records this org originated),
  // false = Target (records this org contributed to / was queried in).
  // Shared across the main dashboard, its drill-down detail pages, and the
  // drill-down drawer.
  fromOrganization: boolean | undefined;
  setFromOrganization: (value: boolean | undefined) => void;
}

export const OrgScopeContext = createContext<OrgScopeContextType | undefined>(
  undefined,
);

// Serializes the tri-state filter to the `from_organization` query param
// value, returning undefined (omit the param entirely) for the "All" state.
export const serializeFromOrganization = (
  value: boolean | undefined,
): string | undefined => (value === undefined ? undefined : String(value));

export const OrgScopeProvider = ({ children }: { children: ReactNode }) => {
  // Initialize from localStorage — works for non-admins (their own org) and
  // as a sensible default for admins before they select a different org.
  const [orgId, setOrgId] = useState<string>(
    () => localStorage.getItem("organizationkey") ?? "",
  );
  const [fromOrganization, setFromOrganization] = useState<
    boolean | undefined
  >(undefined);

  return (
    <OrgScopeContext.Provider
      value={{ orgId, setOrgId, fromOrganization, setFromOrganization }}
    >
      {children}
    </OrgScopeContext.Provider>
  );
};

export const useOrgScope = (): OrgScopeContextType => {
  const ctx = useContext(OrgScopeContext);
  if (!ctx)
    throw new Error("useOrgScope must be used within an OrgScopeProvider");
  return ctx;
};
